import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Container, Image } from "react-bootstrap";
import { Skeleton } from "antd";
import { addToCart } from "../helper/cartfunction";

const ProductCategoryPage = ({
  bannerUrl,
  profileImageUrl,
  categoryName,
  data,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState(searchParams.get("sort") || "");

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setProducts(data);
      // Apply sorting if URL has sort parameter
      if (searchParams.get("sort")) {
        sortProducts(searchParams.get("sort"));
      }
    }
    setLoading(false);
  }, [data]);

  const getDiscountedPrice = (price, discount, discountType) => {
    if (discountType === "percent") {
      return price - (price * discount) / 100;
    } else if (discountType === "amount") {
      return price - discount;
    }
    return price;
  };

  const sortProducts = (option) => {
    let sortedProducts = [...products];
    switch(option) {
      case "name_asc":
        sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name_desc":
        sortedProducts.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "price_asc":
        sortedProducts.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        sortedProducts.sort((a, b) => b.price - a.price);
        break;
      default:
        // Default sorting (original order)
        break;
    }
    setProducts(sortedProducts);
    // Update URL with sorting parameter
    navigate(`?${new URLSearchParams({ sort: option })}`, { replace: true });
  };

  const handleSortChange = (event) => {
    const selectedOption = event.target.value;
    setSortOption(selectedOption);
    sortProducts(selectedOption);
  };

  return (
    <Container fluid className="p-0">
      {/* Banner Section */}
      <div style={{ position: "relative" }}>
        <Image
          src={bannerUrl}
          alt="Category Banner"
          fluid
          className="w-100"
          style={{ height: "300px", objectFit: "cover" }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-50px",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
          }}
        >
          <Image
            src={profileImageUrl}
            alt="Profile"
            roundedCircle
            style={{
              width: "200px",
              height: "200px",
              border: "4px solid white",
            }}
          />
        </div>
      </div>

      {/* Category Title */}
      <Container className="text-center mt-90">
        <h2>{categoryName}</h2>
      </Container>

      {/* Sorting Dropdown */}
      <div className="position-relative text-gray-500 flex-align gap-4 text-14 px-40 mb-30">
        <label htmlFor="sorting" className="text-inherit flex-shrink-0">
          Sort by: 
        </label>
        <select
          className="form-control common-input px-14 py-14 text-inherit rounded-6 w-auto"
          value={sortOption}
          onChange={handleSortChange}
        >
          <option value="">Default</option>
          <option value="name_asc">Name: A-Z</option>
          <option value="name_desc">Name: Z-A</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
        </select>
      </div>

      {/* Product Grid */}
      <div className="row px-40">
        {loading ? (
          // Skeleton loader while loading
          [...Array(8)].map((_, index) => (
            <div key={index} className="col-md-3 mb-4">
              <div className="card shadow-sm p-3">
                <Skeleton.Image style={{ width: "100%", height: "200px" }} />
                <div className="card-body text-center">
                  <Skeleton active paragraph={{ rows: 1 }} />
                  <Skeleton.Button active block />
                </div>
              </div>
            </div>
          ))
        ) : products.length > 0 ? (
          products.map((product) => {
            const discountedPrice = getDiscountedPrice(
              product.price,
              product.discount,
              product.discount_type
            );
            return (
              <div key={product.id} className="col-md-3 mb-20">
                <div className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
                  <Link
                    to={`/product-details/${product.id}`}
                    className="product-card__thumb flex-center rounded-8 bg-white position-relative w-full h-full p-0"
                  >
                    <img
                      src={product.image_fullpath?.[0] || "/placeholder-product.jpg"}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-8"
                    />
                  </Link>
                  <div className="product-card__content mt-16">
                    <h6 className="title text-lg fw-semibold mt-12 mb-8">
                      <Link
                        to={`/product-details/${product.id}`}
                        className="link text-line-2"
                        tabIndex={0}
                      >
                        {product.name}
                      </Link>
                    </h6>
                    <div className="mt-8"></div>
                    <div className="product-card__price my-20">
                      {product.discount > 0 && (
                        <span className="text-gray-400 text-md fw-semibold text-decoration-line-through me-2">
                          Rs.{product.price}
                        </span>
                      )}
                      <span className="text-heading text-md fw-semibold">
                        Rs.{discountedPrice.toFixed(2)}{" "}
                        <span className="text-gray-500 fw-normal">/Qty</span>
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        addToCart(product.id, 1);
                      }}
                      className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-11 px-24 rounded-pill flex-align gap-8 mt-24 w-100 justify-content-center"
                    >
                      Add To Cart <i className="ph ph-shopping-cart" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center my-5">
            <h4>No products found in this category.</h4>
          </div>
        )}
      </div>
    </Container>
  );
};

export default ProductCategoryPage;